'use client'

import React, { useState, useEffect, useRef } from 'react'
import { cn } from '@/utilities/ui'

// Constants for scrollbar
const SCROLLBAR_INDICATOR_WIDTH = 30 // percentage
const SCROLLBAR_MAX_TRANSLATE = (100 - SCROLLBAR_INDICATOR_WIDTH) / (SCROLLBAR_INDICATOR_WIDTH / 100)

/**
 * Hook to enable mouse drag scrolling on a container
 *
 * @param ref - React ref to the scrollable container
 * @param enabled - Whether drag scrolling is enabled
 *
 * @example
 * const scrollRef = useRef<HTMLDivElement>(null)
 * useDragScroll(scrollRef, true)
 *
 * return <div ref={scrollRef} className="overflow-x-auto">...</div>
 */
export function useDragScroll(ref: React.RefObject<HTMLDivElement | null>, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return

    const container = ref.current
    if (!container) return

    let isDown = false
    let dragged = false
    let startX = 0
    let scrollLeft = 0

    const isScrollable = () => container.scrollWidth > container.clientWidth

    const updateCursor = () => {
      container.style.cursor = isScrollable() ? 'grab' : ''
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (!isScrollable()) return
      // Leave sliders and text fields to their own pointer handling
      if ((e.target as HTMLElement).closest('input, textarea, select')) return
      isDown = true
      dragged = false
      container.style.cursor = 'grabbing'
      startX = e.pageX - container.offsetLeft
      scrollLeft = container.scrollLeft
    }

    const handleMouseUp = () => {
      if (!isDown) return
      isDown = false
      updateCursor()
    }

    const handleMouseLeave = () => {
      if (!isDown) return
      isDown = false
      // The release lands outside, so no click arrives to clear the flag
      dragged = false
      updateCursor()
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - container.offsetLeft
      const walk = (x - startX) * 1.5
      if (Math.abs(walk) > 5) dragged = true
      container.scrollLeft = scrollLeft - walk
    }

    // A drag that ends over a button shouldn't also click it
    const handleClick = (e: MouseEvent) => {
      if (!dragged) return
      dragged = false
      e.preventDefault()
      e.stopPropagation()
    }

    const resizeObserver = new ResizeObserver(updateCursor)
    resizeObserver.observe(container)

    updateCursor()

    container.addEventListener('mousedown', handleMouseDown)
    container.addEventListener('mouseleave', handleMouseLeave)
    container.addEventListener('mouseup', handleMouseUp)
    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('click', handleClick, true)

    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      container.removeEventListener('mouseleave', handleMouseLeave)
      container.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('click', handleClick, true)
      resizeObserver.disconnect()
      container.style.cursor = ''
    }
  }, [ref, enabled])
}

/**
 * Hook to track scroll progress and visibility of a scrollable container
 *
 * @param ref - React ref to the scrollable container
 * @param enabled - Whether scroll tracking is enabled
 * @returns Object with scrollProgress (0-1) and isVisible boolean
 *
 * @example
 * const scrollRef = useRef<HTMLDivElement>(null)
 * const { scrollProgress, isVisible } = useScrollProgress(scrollRef, true)
 */
export function useScrollProgress(ref: React.RefObject<HTMLDivElement | null>, enabled: boolean = true) {
  const [scrollProgress, setScrollProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!enabled) return

    const container = ref.current
    if (!container) return

    const checkScrollable = () => {
      const isDisplayed = container.offsetParent !== null || container.offsetWidth > 0
      const isScrollable = isDisplayed && container.scrollWidth > container.clientWidth
      setIsVisible(isScrollable)
    }

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container
      const maxScroll = scrollWidth - clientWidth
      setScrollProgress(maxScroll > 0 ? scrollLeft / maxScroll : 0)
    }

    checkScrollable()
    handleScroll()

    container.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', checkScrollable)

    const resizeObserver = new ResizeObserver(() => {
      checkScrollable()
      handleScroll()
    })
    resizeObserver.observe(container)

    return () => {
      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', checkScrollable)
      resizeObserver.disconnect()
    }
  }, [ref, enabled])

  return { scrollProgress, isVisible }
}

interface ScrollbarProps {
  /** Scroll progress from 0 to 1 */
  scrollProgress: number
  /** Whether the scrollbar should be visible */
  isVisible: boolean
  /** Additional CSS classes for the track */
  className?: string
  /** Color of the track background */
  trackColor?: string
  /** Color of the indicator */
  indicatorColor?: string
}

/**
 * Horizontal scrollbar indicator component
 *
 * @example
 * const { scrollProgress, isVisible } = useScrollProgress(scrollRef, true)
 * <Scrollbar scrollProgress={scrollProgress} isVisible={isVisible} />
 */
export function Scrollbar({
  scrollProgress,
  isVisible,
  className,
  trackColor = 'bg-civo-cloud',
  indicatorColor = 'bg-black',
}: ScrollbarProps) {
  if (!isVisible) return null

  const translateX = Math.min(scrollProgress * SCROLLBAR_MAX_TRANSLATE, SCROLLBAR_MAX_TRANSLATE)

  return (
    <div className={cn('w-full h-[1px] relative overflow-hidden', trackColor, className)}>
      <div
        className={cn('absolute top-0 left-0 h-full transition-all duration-150 ease-out', indicatorColor)}
        style={{
          width: `${SCROLLBAR_INDICATOR_WIDTH}%`,
          transform: `translateX(${translateX}%)`,
        }}
      />
    </div>
  )
}

interface HorizontalScrollContainerProps {
  children: React.ReactNode
  /** Gap between items */
  gap?: string
  /** Additional CSS classes for the scroll container */
  className?: string
  /** Additional CSS classes for the scrollbar */
  scrollbarClassName?: string
  /** Whether to show the scrollbar */
  showScrollbar?: boolean
  /** Whether to enable drag scrolling */
  enableDragScroll?: boolean
  /** Color of the scrollbar track */
  trackColor?: string
  /** Color of the scrollbar indicator */
  indicatorColor?: string
}

/**
 * A horizontal scroll container with drag scrolling and custom scrollbar
 *
 * @example
 * <HorizontalScrollContainer gap="gap-6" scrollbarClassName="mt-8">
 *   {items.map(item => <Card key={item.id} {...item} />)}
 * </HorizontalScrollContainer>
 */
export function HorizontalScrollContainer({
  children,
  gap = 'gap-6',
  className,
  scrollbarClassName = 'mt-16',
  showScrollbar = true,
  enableDragScroll = true,
  trackColor,
  indicatorColor,
}: HorizontalScrollContainerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  useDragScroll(scrollRef, enableDragScroll)
  const { scrollProgress, isVisible } = useScrollProgress(scrollRef, showScrollbar)

  return (
    <>
      <div
        ref={scrollRef}
        className={cn('flex overflow-x-auto scrollbar-hide', gap, className)}
      >
        {children}
      </div>
      {showScrollbar && (
        <Scrollbar
          scrollProgress={scrollProgress}
          isVisible={isVisible}
          className={scrollbarClassName}
          trackColor={trackColor}
          indicatorColor={indicatorColor}
        />
      )}
    </>
  )
}
